import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  GUEST_SOURCES,
  GUEST_STATUSES,
  formatAmount,
  parseAmountToCents,
  type GuestSource,
  type GuestStatus,
  type Order,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCurrency } from '../lib/currency';
import { useToast } from '../hooks/useToast';
import { Badge, Button, Field, Input, Modal } from './ui';

/**
 * The guest mix — how the guest found us and whether they have been before —
 * plus the tip. None of it is part of the bill: it never reaches the receipt,
 * the e-invoice or the takings, only the history, the reports and the daily
 * report push.
 */

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'h-10 rounded-lg border px-3 text-sm font-medium transition',
        active
          ? 'border-accent bg-accent/15 text-accent-strong'
          : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** Source and New/Return pickers. Tapping the selected chip again clears it. */
export function GuestMixFields({
  source,
  status,
  onSourceChange,
  onStatusChange,
}: {
  source: GuestSource | null;
  status: GuestStatus | null;
  onSourceChange: (source: GuestSource | null) => void;
  onStatusChange: (status: GuestStatus | null) => void;
}) {
  return (
    <div className="space-y-4">
      <Field label="How did they find us? (optional)">
        <div className="flex flex-wrap gap-2">
          {GUEST_SOURCES.map((candidate) => (
            <Chip
              key={candidate}
              active={source === candidate}
              onClick={() => onSourceChange(source === candidate ? null : candidate)}
            >
              {candidate}
            </Chip>
          ))}
        </div>
      </Field>
      <Field label="Guest (optional)">
        <div className="flex flex-wrap gap-2">
          {GUEST_STATUSES.map((candidate) => (
            <Chip
              key={candidate}
              active={status === candidate}
              onClick={() => onStatusChange(status === candidate ? null : candidate)}
            >
              {candidate === 'New' ? 'New guest' : 'Returning guest'}
            </Chip>
          ))}
        </div>
      </Field>
    </div>
  );
}

/** Compact source / New-Return tags for a table row. */
export function GuestMixBadges({ source, status }: { source: GuestSource | null; status: GuestStatus | null }) {
  if (!source && !status) return <span className="text-ink-faint">—</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {source ? <Badge tone="neutral">{source}</Badge> : null}
      {status ? <Badge tone={status === 'New' ? 'positive' : 'accent'}>{status}</Badge> : null}
    </div>
  );
}

/** A money field for the tip, in the restaurant's currency. Returns NaN while unparseable. */
export function useTipInput(initialCents: number, open: boolean) {
  const { decimals } = useCurrency();
  const [text, setText] = useState('');

  useEffect(() => {
    if (open) setText(initialCents > 0 ? formatAmount(initialCents, decimals) : '');
  }, [open, initialCents, decimals]);

  const cents = useMemo(() => {
    if (!text.trim()) return 0;
    try {
      const parsed = parseAmountToCents(text);
      return parsed >= 0 ? parsed : NaN;
    } catch {
      return NaN;
    }
  }, [text]);

  return { text, setText, cents, valid: Number.isFinite(cents) };
}

export function TipField({ tip }: { tip: ReturnType<typeof useTipInput> }) {
  const { symbol } = useCurrency();
  return (
    <Field
      label="Tip (optional)"
      hint="Kept off the bill — not on the receipt or the invoice."
      error={!tip.valid ? 'Enter an amount such as 50,000' : undefined}
    >
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint">{symbol}</span>
        <Input
          inputMode="decimal"
          className="pl-8"
          placeholder="0"
          value={tip.text}
          onChange={(event) => tip.setText(event.target.value)}
        />
      </div>
    </Field>
  );
}

/** Edit an order's source, New/Return and tip — also after it was closed. */
export function GuestMixDialog({ order, open, onClose }: { order: Order; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<GuestSource | null>(order.guestSource);
  const [status, setStatus] = useState<GuestStatus | null>(order.guestStatus);
  const tip = useTipInput(order.tipCents, open);

  useEffect(() => {
    if (!open) return;
    setSource(order.guestSource);
    setStatus(order.guestStatus);
  }, [open, order.guestSource, order.guestStatus]);

  const save = useMutation({
    mutationFn: () =>
      api.orders.update(order.id, { guestSource: source, guestStatus: status, tipCents: tip.cents }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      toast.success('Guest details saved.');
      onClose();
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not save the guest details.'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Guest & tip"
      subtitle={`Order #${order.orderNumber} · Table ${order.tableNumber}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!tip.valid} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <GuestMixFields source={source} status={status} onSourceChange={setSource} onStatusChange={setStatus} />
        <TipField tip={tip} />
      </div>
    </Modal>
  );
}
